// Simple in-memory media cache to avoid re-downloading files repeatedly within a session.
// Keys can be CR_REF (number) or explicit URLs. Values are { url: objectUrl, type: mime, kind: 'image'|'video'|'other', blobSize }.

import { timeoutMessage } from './timeoutMessage';

const _cache = new Map();

const VIDEO_EXTS = ['mp4','webm','ogg','mov','avi','mkv','m4v'];
const IMAGE_EXTS = ['png','jpg','jpeg','gif','bmp','webp','svg'];
const PDF_EXTS = ['pdf'];
const OFFICE_EXTS = ['doc','docx','xls','xlsx','ppt','pptx','odt','ods','odp'];

const OFFICE_ACCEPT = [
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp',
];

/** Cari resim/doküman yükleyici accept listesi (görsel, video, PDF, Office). */
export const CARI_MEDIA_ACCEPT = [
  'image/*',
  'video/*',
  'application/pdf',
  ...OFFICE_ACCEPT,
].join(',');

/** Müşteri menüsü → Dökümanlar paneli (PDF + Office). */
export const DOKUMAN_ACCEPT = [
  'application/pdf',
  ...OFFICE_ACCEPT,
].join(',');

function _extFromPath(path) {
  const m = /\.([a-zA-Z0-9]+)$/.exec(path || '');
  return m ? m[1].toLowerCase() : '';
}

function _kindFromMimeOrPath(mime, path) {
  if (mime) {
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
  }
  if (path) {
    const ext = _extFromPath(path);
    if (VIDEO_EXTS.includes(ext)) return 'video';
    if (IMAGE_EXTS.includes(ext)) return 'image';
    if (PDF_EXTS.includes(ext)) return 'pdf';
    if (OFFICE_EXTS.includes(ext)) return 'office';
  }
  return 'other';
}

/** path uzantısından medya türü: image | video | pdf | office | other */
export function getMediaKindFromPath(path) {
  return _kindFromMimeOrPath(null, path);
}

export async function getByDownloadRef(baseUrl, crRef, pathHint) {
  const key = `ref:${crRef}`;
  if (_cache.has(key)) return _cache.get(key);

  const url = `${baseUrl}fileops/download?CR_REF=${crRef}`;
  const headers = {};
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const resp = await fetch(url, { cache: 'no-store', headers });
  if (!resp.ok) throw new Error(resp.status === 504 ? timeoutMessage() : `Media fetch failed (${resp.status})`);
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const kind = _kindFromMimeOrPath(blob.type, pathHint);
  const value = { url: objectUrl, type: blob.type || '', kind, blobSize: blob.size };
  _cache.set(key, value);
  return value;
}

export async function getByDirectUrl(directUrl, pathHint) {
  const key = `url:${directUrl}`;
  if (_cache.has(key)) return _cache.get(key);
  const headers = {};
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const resp = await fetch(directUrl, { cache: 'no-store', headers });
  if (!resp.ok) throw new Error(resp.status === 504 ? timeoutMessage() : `Media fetch failed (${resp.status})`);
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const kind = _kindFromMimeOrPath(blob.type, pathHint);
  const value = { url: objectUrl, type: blob.type || '', kind, blobSize: blob.size };
  _cache.set(key, value);
  return value;
}

export function revokeAll() {
  for (const [, v] of _cache) {
    try { URL.revokeObjectURL(v.url); } catch {}
  }
  _cache.clear();
}

export function isVideoPath(path) {
  return VIDEO_EXTS.includes(_extFromPath(path));
}

export function isImagePath(path) {
  return IMAGE_EXTS.includes(_extFromPath(path));
}

export function isPdfPath(path) {
  return PDF_EXTS.includes(_extFromPath(path));
}

export function isOfficePath(path) {
  return OFFICE_EXTS.includes(_extFromPath(path));
}
